/**
 * Subscription Service
 *
 * Handles all subscription-related API calls to the Subscription API
 */

import httpClient from './httpClient';
import tenantConfig from '../../config/tenantConfig';

/**
 * Get subscription API base URL
 */
const getSubscriptionApiUrl = () => {
  const config = tenantConfig.getTenantConfig();
  const baseUrl = config.api.subscription;
  if (!baseUrl) {
    throw new Error('Subscription API URL not configured. Run bin/frontend/configure-api-endpoints.sh');
  }
  return baseUrl;
};

/**
 * Create Stripe Checkout Session
 * @param {string} priceId - Stripe price ID (e.g., price_xxx)
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if user cancels
 * @param {object} metadata - Optional metadata to attach to subscription
 * @returns {Promise<{sessionId: string, url: string}>} Checkout session details
 */
export async function createCheckoutSession(priceId, successUrl, cancelUrl, metadata = {}) {
  const response = await httpClient.post(`${getSubscriptionApiUrl()}/subscription/create-checkout`, {
    priceId,
    successUrl,
    cancelUrl,
    metadata
  });
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * Create Stripe Customer Portal Session
 * @param {string} returnUrl - URL to return to after portal session
 * @returns {Promise<{url: string}>} Portal session URL
 */
export async function createPortalSession(returnUrl) {
  const response = await httpClient.post(`${getSubscriptionApiUrl()}/subscription/portal`, {
    returnUrl
  });
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * Get current user's subscription status
 * @returns {Promise<object|null>} Subscription object or null if no subscription
 */
export async function getSubscriptionStatus() {
  try {
    const response = await httpClient.get(`${getSubscriptionApiUrl()}/subscription`);
    // httpClient interceptor returns response.data directly
    return response;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No subscription found
    }
    throw error;
  }
}

/**
 * Get subscription by ID
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<object>} Subscription object
 */
export async function getSubscriptionById(subscriptionId) {
  const response = await httpClient.get(`${getSubscriptionApiUrl()}/subscription/${subscriptionId}`);
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * List all subscriptions (admin only)
 * @param {object} filters - Optional filters (status, userId, etc.)
 * @returns {Promise<Array>} Array of subscription objects
 */
export async function listSubscriptions(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  const url = params
    ? `${getSubscriptionApiUrl()}/subscription/list?${params}`
    : `${getSubscriptionApiUrl()}/subscription/list`;

  const response = await httpClient.get(url);
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * Cancel subscription at end of current period
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<object>} Updated subscription object
 */
export async function cancelSubscription(subscriptionId) {
  const response = await httpClient.post(`${getSubscriptionApiUrl()}/subscription/${subscriptionId}/cancel`);
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * Reactivate a canceled subscription
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<object>} Updated subscription object
 */
export async function reactivateSubscription(subscriptionId) {
  const response = await httpClient.post(`${getSubscriptionApiUrl()}/subscription/${subscriptionId}/reactivate`);
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * Sync subscription from Stripe (admin only - manual sync)
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<object>} Updated subscription object
 */
export async function syncSubscriptionFromStripe(subscriptionId) {
  const response = await httpClient.post(`${getSubscriptionApiUrl()}/subscription/${subscriptionId}/sync`);
  // httpClient interceptor returns response.data directly
  return response;
}

/**
 * Get available subscription plans for current tenant
 * Returns active plans matching tenant's current Stripe mode (test/live)
 * @returns {Promise<Array>} Array of plan objects
 */
export async function getSubscriptionPlans() {
  try {
    // httpClient interceptor returns response.data directly
    const data = await httpClient.get(`${getSubscriptionApiUrl()}/subscription/plans`);
    return data.plans || [];
  } catch (error) {
    console.error('Failed to fetch subscription plans:', error);
    return [];
  }
}

export default {
  createCheckoutSession,
  createPortalSession,
  getSubscriptionStatus,
  getSubscriptionById,
  listSubscriptions,
  cancelSubscription,
  reactivateSubscription,
  syncSubscriptionFromStripe,
  getSubscriptionPlans
};
